import { useEffect, useMemo, useState } from 'react';
import { Archive, Building, Calendar, ChevronDown, ChevronUp, Link as LinkIcon, Loader2, RefreshCw, Search, Trash2, User, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatDateDDMMYYYY } from '../utils/format';
import { NotificationModal } from './NotificationModal';
import { ConfirmationModal } from './ConfirmationModal';

interface ManualBackupSummary {
  id: string;
  original_resident_id: string;
  building: string;
  room_number: string;
  position: number | null;
  name: string;
  phone: string | null;
  study: string | null;
  start_date: string | null;
  payment_pattern: number | null;
  backed_up_at: string;
  backed_up_by: string | null;
  backed_up_by_email: string | null;
  backup_reason: string | null;
  total_payments: number;
  first_payment_date: string | null;
  last_payment_date: string | null;
}

interface ManualBackupDetailPayment {
  id: string;
  original_payment_id: string;
  manual_backup_resident_id: string;
  original_resident_id: string;
  period_number: number;
  payment_proof_date: string | null;
  payment_proof_link: string | null;
  next_payment_date: string | null;
  category: string | null;
  installment_number: number | null;
  backed_up_at: string;
  original_created_at: string | null;
  original_updated_at: string | null;
}

interface ManualBackupDetailResponse {
  backup_info: ManualBackupSummary & {
    notes: string | null;
  };
  payments: ManualBackupDetailPayment[] | null;
}

const BUILDING_LABELS: Record<string, string> = {
  gajah3: 'Gajah 3',
  gajah4: 'Gajah 4',
  anduring: 'Anduring',
  limau_manis: 'Limau Manis'
};

const BUILDING_OPTIONS = [
  { value: 'all', label: 'Semua Gedung' },
  { value: 'gajah3', label: 'Gajah 3' },
  { value: 'gajah4', label: 'Gajah 4' },
  { value: 'anduring', label: 'Anduring' },
  { value: 'limau_manis', label: 'Limau Manis' }
];

export function ManualBackupsHistory({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const [backups, setBackups] = useState<ManualBackupSummary[]>([]);
  const [filteredBackups, setFilteredBackups] = useState<ManualBackupSummary[]>([]);
  const [expandedBackupId, setExpandedBackupId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ManualBackupDetailResponse | null>(null);
  const [search, setSearch] = useState('');
  const [building, setBuilding] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    visible: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
  }>({ visible: false, type: 'success', title: '', message: '' });
  const [confirmation, setConfirmation] = useState<{
    visible: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    if (visible) {
      setExpandedBackupId(null);
      setDetail(null);
      loadBackups();
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    const lowerSearch = search.trim().toLowerCase();
    const next = backups.filter((backup) => {
      const matchesSearch = !lowerSearch ||
        backup.name.toLowerCase().includes(lowerSearch) ||
        backup.room_number.toLowerCase().includes(lowerSearch) ||
        (backup.phone ?? '').toLowerCase().includes(lowerSearch) ||
        (backup.backup_reason ?? '').toLowerCase().includes(lowerSearch);

      const matchesBuilding = building === 'all' || backup.building === building;

      return matchesSearch && matchesBuilding;
    });

    setFilteredBackups(next);
  }, [backups, search, building, visible]);

  const loadBackups = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .from('v_manual_backup_residents_summary')
        .select('*')
        .order('backed_up_at', { ascending: false });

      if (queryError) throw queryError;
      setBackups((data || []) as ManualBackupSummary[]);
    } catch (err: any) {
      console.error('Failed to load manual backups:', err);
      setError(err.message || 'Gagal memuat data backup manual');
    } finally {
      setLoading(false);
    }
  };

  const loadBackupDetail = async (backupId: string) => {
    try {
      setDetailLoading(true);
      setError(null);

      const { data, error: queryError } = await supabase
        .rpc('get_backup_detail', { p_backup_id: backupId });

      if (queryError) throw queryError;
      setDetail((data as ManualBackupDetailResponse) || null);
    } catch (err: any) {
      console.error('Failed to load manual backup detail:', err);
      setError(err.message || 'Gagal memuat detail backup');
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleDetail = async (backup: ManualBackupSummary) => {
    if (expandedBackupId === backup.id) {
      setExpandedBackupId(null);
      setDetail(null);
      return;
    }

    setExpandedBackupId(backup.id);
    await loadBackupDetail(backup.id);
  };

  const deleteBackup = async (backupId: string, residentName: string) => {
    setConfirmation({
      visible: true,
      title: 'Hapus Backup',
      message: `Anda akan menghapus backup untuk penghuni "${residentName}".\n\nSemua data backup termasuk riwayat pembayaran akan dihapus permanen.\n\nLanjutkan hapus backup?`,
      onConfirm: async () => {
        try {
          setDeletingId(backupId);
          setError(null);

          const { data, error: deleteError } = await supabase
            .rpc('delete_manual_backup', { p_backup_id: backupId });

          if (deleteError) throw deleteError;

          if (data && data.success) {
            setNotification({
              visible: true,
              type: 'success',
              title: 'Backup Berhasil Dihapus!',
              message: `Data backup untuk "${residentName}" telah dihapus.`
            });
            
            // Tutup detail jika sedang dibuka
            if (expandedBackupId === backupId) {
              setExpandedBackupId(null);
              setDetail(null);
        }
        
        // Reload daftar backup
        await loadBackups();
      } else {
        throw new Error(data?.message || 'Gagal menghapus backup');
      }
    } catch (err: any) {
      console.error('Failed to delete backup:', err);
      setNotification({
        visible: true,
        type: 'error',
        title: 'Gagal Menghapus Backup',
        message: err.message || 'Terjadi kesalahan. Silakan coba lagi.'
      });
    } finally {
      setDeletingId(null);
    }
      }
    });
  };

  const backupCountByBuilding = useMemo(() => {
    return backups.reduce<Record<string, number>>((acc, backup) => {
      acc[backup.building] = (acc[backup.building] || 0) + 1;
      return acc;
    }, {});
  }, [backups]);

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/60">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Riwayat backup manual data penghuni</p>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Manual Backup History
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-300"
            aria-label="Tutup"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 border-b dark:border-gray-800 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="flex flex-1 items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm flex-1">
              <Search className="h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari nama, kamar, atau alasan backup..."
                className="flex-1 bg-transparent focus:outline-none text-sm text-gray-700 dark:text-gray-200"
              />
            </div>
            <div className="px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
              <select
                value={building}
                onChange={(e) => setBuilding(e.target.value)}
                className="bg-transparent focus:outline-none text-sm text-gray-700 dark:text-gray-200"
              >
                {BUILDING_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              {Object.entries(BUILDING_LABELS).map(([value, label]) => (
                <div key={value} className="flex items-center gap-1">
                  <Building className="h-3.5 w-3.5" />
                  <span>{label}:</span>
                  <span className="font-semibold text-gray-700 dark:text-gray-200">{backupCountByBuilding[value] || 0}</span>
                </div>
              ))}
            </div>
            <button
              onClick={loadBackups}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/40 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 gap-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p>Memuat data backup manual...</p>
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center text-red-500 gap-2 px-6 text-center">
              <p className="font-medium">{error}</p>
              <button
                onClick={loadBackups}
                className="px-3 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Coba Lagi
              </button>
            </div>
          ) : filteredBackups.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 gap-2 px-6 text-center">
              <Archive className="h-8 w-8" />
              <p className="font-medium">Belum ada backup manual yang terekam.</p>
              <p className="text-sm">Gunakan tombol backup di kamar untuk membuat salinan data penghuni.</p>
            </div>
          ) : (
            <div className="divide-y dark:divide-gray-800">
              {filteredBackups.map((backup) => {
                const isExpanded = expandedBackupId === backup.id;
                const detailInfo = isExpanded ? detail : null;

                return (
                  <div key={backup.id} className="p-4 sm:p-6 bg-white dark:bg-gray-900">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 flex items-center gap-1">
                            <Building className="h-3.5 w-3.5" />
                            {BUILDING_LABELS[backup.building] || backup.building}
                          </span>
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                            Kamar {backup.room_number}{backup.position ? ` · Penghuni ${backup.position}` : ''}
                          </span>
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDateDDMMYYYY(backup.backed_up_at)}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                          <User className="h-5 w-5 text-gray-400" />
                          {backup.name}
                        </h3>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
                          <p><span className="font-medium text-gray-700 dark:text-gray-200">Telepon:</span> {backup.phone || '—'}</p>
                          <p><span className="font-medium text-gray-700 dark:text-gray-200">Program:</span> {backup.study || '—'}</p>
                          <p><span className="font-medium text-gray-700 dark:text-gray-200">Mulai Tinggal:</span> {backup.start_date ? formatDateDDMMYYYY(backup.start_date) : '—'}</p>
                          <p><span className="font-medium text-gray-700 dark:text-gray-200">Total Bukti Pembayaran:</span> {backup.total_payments}</p>
                          <p className="md:col-span-2"><span className="font-medium text-gray-700 dark:text-gray-200">Alasan Backup:</span> {backup.backup_reason || '—'}</p>
                        </div>
                        {backup.backed_up_by_email && (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Dibackup oleh: {backup.backed_up_by_email}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-stretch gap-2 min-w-[160px]">
                        <button
                          onClick={() => toggleDetail(backup)}
                          className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors
                            border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200
                            hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          {detailLoading && isExpanded ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                          )}
                          {isExpanded ? 'Tutup Detail' : 'Lihat Detail'}
                        </button>
                        <button
                          onClick={() => deleteBackup(backup.id, backup.name)}
                          disabled={deletingId === backup.id}
                          className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors
                            border-red-300 dark:border-red-500/60 text-red-600 dark:text-red-400
                            hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingId === backup.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                          {deletingId === backup.id ? 'Menghapus...' : 'Hapus Backup'}
                        </button>
                        {backup.total_payments > 0 && detailInfo?.payments && detailInfo.payments.some(p => p.payment_proof_link) && (
                          <a
                            href={detailInfo.payments.find(p => p.payment_proof_link)?.payment_proof_link || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-300 border border-blue-300 dark:border-blue-500/60 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          >
                            <LinkIcon className="h-4 w-4" />
                            Bukti Pembayaran
                          </a>
                        )}
                      </div>
                    </div>

                    {isExpanded && detailInfo && (
                      <div className="mt-4 border-t pt-4 dark:border-gray-800">
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Detail Pembayaran Tersalin</h4>
                        {detailInfo.payments && detailInfo.payments.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300">
                                <tr>
                                  <th className="py-2 px-3 text-left">Periode</th>
                                  <th className="py-2 px-3 text-left">Kategori</th>
                                  <th className="py-2 px-3 text-left">Tanggal Bukti</th>
                                  <th className="py-2 px-3 text-left">Jatuh Tempo Berikutnya</th>
                                  <th className="py-2 px-3 text-left">Link Bukti</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y dark:divide-gray-800">
                                {detailInfo.payments.map(payment => (
                                  <tr key={payment.id}>
                                    <td className="py-2 px-3 text-gray-700 dark:text-gray-200">Periode {payment.period_number}</td>
                                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{payment.category ? (payment.category === 'Cicilan' && payment.installment_number ? `${payment.category} ke-${payment.installment_number}` : payment.category) : '—'}</td>
                                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{payment.payment_proof_date ? formatDateDDMMYYYY(payment.payment_proof_date) : '—'}</td>
                                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{payment.next_payment_date ? formatDateDDMMYYYY(payment.next_payment_date) : '—'}</td>
                                    <td className="py-2 px-3">
                                      {payment.payment_proof_link ? (
                                        <a
                                          href={payment.payment_proof_link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 dark:text-blue-300 hover:underline"
                                        >
                                          Lihat Bukti
                                        </a>
                                      ) : (
                                        <span className="text-gray-400">Tidak ada</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400">Penghuni ini belum memiliki riwayat pembayaran yang dibackup.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      <NotificationModal
        visible={notification.visible}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification({ ...notification, visible: false })}
      />
      
      {confirmation && (
        <ConfirmationModal
          visible={confirmation.visible}
          type="danger"
          title={confirmation.title}
          message={confirmation.message}
          onConfirm={confirmation.onConfirm}
          onCancel={() => setConfirmation(null)}
        />
      )}
    </div>
  );
}
